// Hand-written validators for board content (spec §7.2). The server runs
// these on everything a client sends; nothing here trusts its input.
import type { BoardObject, ObjectType } from './types.ts';
import { MAX_RICH_TEXT_BYTES, sanitizeHref } from './rich-text.ts';
import { CARD_LIMITS, isValidDue, ITEM_ID, ITEM_KEYS } from './cards.ts';
import { AUDIO, AUDIO_TYPES } from './audio.ts';

export const LIMITS = {
  objectsPerBoard: 50_000,
  richTextBytes: MAX_RICH_TEXT_BYTES,
  freehandPoints: 5_000,
  frameChildren: 50_000,
  coord: 1e8,         // |x|, |y|
  size: 1e7,          // w, h
  titleLength: 200,
} as const;

const TYPES = new Set<ObjectType>(['sticky', 'shape', 'text', 'connector', 'image', 'audio', 'freehand', 'frame', 'card']);
const AUDIO_MIMES = new Set<string>(AUDIO_TYPES);
const SHAPE_KINDS = new Set(['rect', 'ellipse', 'diamond', 'triangle']);
const ID = /^[A-Za-z0-9_-]{1,64}$/;
const Z = /^[0-9A-Za-z]{1,128}$/;
const COLOR = /^(#[0-9a-fA-F]{3,8}|transparent)$/;
const HASH = /^[0-9a-f]{64}$/;

type Rec = Record<string, unknown>;
const isRec = (v: unknown): v is Rec => typeof v === 'object' && v !== null && !Array.isArray(v);
const num = (v: unknown, min: number, max: number) => typeof v === 'number' && Number.isFinite(v) && v >= min && v <= max;
const optBool = (v: unknown) => v === undefined || typeof v === 'boolean';

export function validateRichText(v: unknown): string | null {
  if (!isRec(v) || !Array.isArray(v.blocks)) return 'text must be { blocks: [] }';
  for (const b of v.blocks) {
    if (!isRec(b) || !Array.isArray(b.runs)) return 'text block must have runs';
    if (b.list !== undefined && b.list !== 'bullet') return 'text block list must be "bullet"';
    for (const r of b.runs) {
      if (!isRec(r) || typeof r.t !== 'string') return 'text run must have a string t';
      if (r.b !== undefined && r.b !== 1) return 'text run b must be 1';
      if (r.i !== undefined && r.i !== 1) return 'text run i must be 1';
      if (r.href !== undefined && (typeof r.href !== 'string' || sanitizeHref(r.href) !== r.href)) return 'text run href is not allowed';
    }
  }
  if (JSON.stringify(v).length > LIMITS.richTextBytes) return `text exceeds ${LIMITS.richTextBytes} bytes`;
  return null;
}

function validateEndpoint(v: unknown): string | null {
  if (!isRec(v)) return 'endpoint must be an object';
  if ('objectId' in v) {
    if (typeof v.objectId !== 'string' || !ID.test(v.objectId)) return 'endpoint objectId is invalid';
    const a = v.anchor;
    if (!Array.isArray(a) || a.length !== 2 || !a.every((n) => num(n, 0, 1))) return 'endpoint anchor must be [0..1, 0..1]';
    return null;
  }
  const p = v.point;
  if (!Array.isArray(p) || p.length !== 2 || !p.every((n) => num(n, -LIMITS.coord, LIMITS.coord))) return 'endpoint point is invalid';
  return null;
}

const ITEM_KEY_SET = new Set<string>(ITEM_KEYS);

function validateItem(v: unknown): string | null {
  if (!isRec(v)) return 'checklist item must be an object';
  for (const k of Object.keys(v)) if (!ITEM_KEY_SET.has(k)) return `checklist item has an unknown field ${k}`;
  if (typeof v.text !== 'string' || v.text.length > CARD_LIMITS.itemLength) return 'checklist item text is invalid';
  if (typeof v.done !== 'boolean') return 'checklist item done must be boolean';
  if (typeof v.order !== 'string' || !Z.test(v.order)) return 'checklist item order is invalid';
  if (v.assignee !== undefined && (typeof v.assignee !== 'string' || !ID.test(v.assignee))) return 'checklist item assignee is invalid';
  return null;
}

function validateCard(v: Rec): string | null {
  if (typeof v.title !== 'string' || v.title.length > CARD_LIMITS.titleLength) return 'title is invalid';
  if (v.note !== undefined) {
    const err = validateRichText(v.note);
    if (err) return `note: ${err}`;
  }
  if (typeof v.color !== 'string' || !COLOR.test(v.color)) return 'color is invalid';
  if (!isRec(v.items)) return 'items must be an object';
  const ids = Object.keys(v.items);
  if (ids.length > CARD_LIMITS.items) return `a card holds at most ${CARD_LIMITS.items} checklist items`;
  for (const id of ids) {
    if (!ITEM_ID.test(id)) return 'checklist item id is invalid';
    const err = validateItem(v.items[id]);
    if (err) return err;
  }
  const a = v.assignees;
  if (!Array.isArray(a) || a.length > CARD_LIMITS.assignees) return `a card has at most ${CARD_LIMITS.assignees} assignees`;
  if (!a.every((u) => typeof u === 'string' && ID.test(u)) || new Set(a).size !== a.length) return 'assignees are invalid';
  if (v.due !== undefined && (typeof v.due !== 'string' || !isValidDue(v.due))) return 'due must be a date (YYYY-MM-DD)';
  return null;
}

/** Returns an error message, or null if `v` is a valid BoardObject. */
export function validateObject(v: unknown): string | null {
  if (!isRec(v)) return 'object must be an object';
  if (typeof v.id !== 'string' || !ID.test(v.id)) return 'id is invalid';
  const where = (msg: string) => `${v.id}: ${msg}`;
  if (!TYPES.has(v.type as ObjectType)) return where('type is invalid');
  if (!num(v.x, -LIMITS.coord, LIMITS.coord) || !num(v.y, -LIMITS.coord, LIMITS.coord)) return where('position is invalid');
  if (!num(v.w, 0, LIMITS.size) || !num(v.h, 0, LIMITS.size)) return where('size is invalid');
  if (!num(v.rotation, -100, 100)) return where('rotation is invalid');
  if (typeof v.z !== 'string' || !Z.test(v.z)) return where('z is invalid');
  if (!optBool(v.locked)) return where('locked must be boolean');
  if (typeof v.createdBy !== 'string' || v.createdBy.length > 64) return where('createdBy is invalid');

  const color = (k: string) => typeof v[k] === 'string' && COLOR.test(v[k] as string);
  const text = (optional: boolean) => {
    if (optional && v.text === undefined) return null;
    const err = validateRichText(v.text);
    return err && where(err);
  };

  switch (v.type as ObjectType) {
    case 'sticky':
      if (!color('color')) return where('color is invalid');
      return text(false);
    case 'shape':
      if (!SHAPE_KINDS.has(v.kind as string)) return where('kind is invalid');
      if (!color('fill') || !color('stroke')) return where('fill/stroke is invalid');
      if (!num(v.strokeWidth, 0, 100)) return where('strokeWidth is invalid');
      return text(true);
    case 'text':
      if (!num(v.fontSize, 1, 1000)) return where('fontSize is invalid');
      return text(false);
    case 'image':
      return typeof v.assetHash === 'string' && HASH.test(v.assetHash) ? null : where('assetHash is invalid');
    case 'audio': {
      if (typeof v.assetHash !== 'string' || !HASH.test(v.assetHash)) return where('assetHash is invalid');
      if (typeof v.mime !== 'string' || !AUDIO_MIMES.has(v.mime)) return where('mime is invalid');
      if (!num(v.duration, 0, AUDIO.maxSeconds + AUDIO.slackSeconds)) return where('duration is invalid');
      const p = v.peaks;
      if (!Array.isArray(p) || p.length > AUDIO.peaks || !p.every((n) => num(n, 0, 1))) return where('peaks is invalid');
      if (typeof v.title !== 'string' || v.title.length > LIMITS.titleLength) return where('title is invalid');
      return null;
    }
    case 'freehand': {
      const p = v.points;
      if (!Array.isArray(p) || p.length % 2 !== 0 || p.length > LIMITS.freehandPoints * 2) return where('points is invalid');
      if (!p.every((n) => num(n, -LIMITS.size, LIMITS.size))) return where('points must be finite numbers');
      if (!color('stroke') || !num(v.strokeWidth, 0, 100)) return where('stroke is invalid');
      return null;
    }
    case 'frame':
      if (typeof v.title !== 'string' || v.title.length > LIMITS.titleLength) return where('title is invalid');
      if (!Array.isArray(v.children) || v.children.length > LIMITS.frameChildren ||
        !v.children.every((c) => typeof c === 'string' && ID.test(c))) return where('children is invalid');
      if (v.layout !== undefined && v.layout !== 'column') return where('layout is invalid');
      return null;
    case 'connector': {
      const err = validateEndpoint(v.from) ?? validateEndpoint(v.to);
      if (err) return where(err);
      if (v.routing !== 'straight' && v.routing !== 'elbow') return where('routing is invalid');
      if (!color('stroke')) return where('stroke is invalid');
      if (typeof v.arrowStart !== 'boolean' || typeof v.arrowEnd !== 'boolean') return where('arrows must be boolean');
      return null;
    }
    case 'card': {
      const err = validateCard(v);
      return err && where(err);
    }
  }
}

/** Validates a whole board's objects: count limit, unique ids, each object. */
export function validateObjects(v: unknown): { ok: true; objects: BoardObject[] } | { ok: false; error: string } {
  if (!Array.isArray(v)) return { ok: false, error: 'objects must be an array' };
  if (v.length > LIMITS.objectsPerBoard) return { ok: false, error: `a board holds at most ${LIMITS.objectsPerBoard} objects` };
  const ids = new Set<string>();
  for (const o of v) {
    const err = validateObject(o);
    if (err) return { ok: false, error: err };
    const id = (o as BoardObject).id;
    if (ids.has(id)) return { ok: false, error: `duplicate id ${id}` };
    ids.add(id);
  }
  return { ok: true, objects: v as BoardObject[] };
}
